import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLongPressAction } from "@/hooks/useLongPressAction";
import { ArrowLeft, Flag, Reply, Send, VolumeX, Volume2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import MentionComposerField from "@/components/MentionComposerField";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import FullScreenImage from "@/components/FullScreenImage";
import KindredSharedContent from "@/components/KindredSharedContent";
import MessageActions from "@/components/MessageActions";
import ReactionBar from "@/components/ReactionBar";
import ReplyQuote from "@/components/ReplyQuote";
import ReportSheet from "@/components/ReportSheet";
import { composeKindredContent, getKindredSharePreview, parseKindredShareContent } from "@/lib/kindredShare";
import { dispatchNotification } from "@/lib/pushNotifications";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import { toast } from "sonner";
import type { MentionDraft } from "../../shared/community";
import { canManageOwnedContentOnly } from "../../shared/system";
import { groupScopedReactions } from "../../shared/community";
import { AppButton, AppButtonRow, AppCard, AppCardBody, AppEmptyState, AppInput, AppLoadingState } from "../../ui-foundation";

const MessageThread = () => {
  const { connectionId } = useParams<{ connectionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const longPressAction = useLongPressAction();
  const [otherProfile, setOtherProfile] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [reactions, setReactions] = useState<any[]>([]);
  const [newMsg, setNewMsg] = useState("");
  const [newMsgMentions, setNewMsgMentions] = useState<MentionDraft[]>([]);
  const [photoPath, setPhotoPath] = useState<string | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<any>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMsgId, setReportMsgId] = useState<string | null>(null);
  const [messageActionsId, setMessageActionsId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    if (!connectionId || !user) return;
    const { data: conn } = await supabase.from("connections").select("*").eq("id", connectionId).single();
    if (!conn) return;
    const otherId = conn.requester_id === user.id ? conn.recipient_id : conn.requester_id;
    setIsMuted((conn as any).is_muted ?? false);
    const [{ data: prof }, { data: msgs }] = await Promise.all([
      supabase.from("profiles").select("id, display_name, avatar_url").eq("id", otherId).single(),
      supabase.from("direct_messages").select("*").eq("connection_id", connectionId).order("created_at", { ascending: true }),
    ]);
    const messageIds = (msgs ?? []).map((message: any) => message.id);
    const { data: rxns } = messageIds.length
      ? await supabase.from("direct_message_reactions").select("*").in("message_id", messageIds)
      : { data: [] as any[] };
    if (prof) setOtherProfile(prof);
    setReactions((rxns as any[]) || []);
    if (msgs) {
      const resolvedDmPhotoUrls = await resolveSignedStorageUrls(
        "dm-photos",
        msgs.map((message: any) => message.photo_url),
      );
      setMessages(
        msgs.map((message: any) => ({
          ...message,
          photo_url: resolveStoredObjectUrl("dm-photos", message.photo_url, resolvedDmPhotoUrls),
        })),
      );
    }
    if (msgs && msgs.length > 0) {
      const unreadIds = msgs.filter((m: any) => m.sender_id !== user.id && !m.read_at).map((m: any) => m.id);
      if (unreadIds.length > 0) {
        await supabase.rpc("mark_direct_messages_read", { message_ids: unreadIds });
      }
    }
  }, [connectionId, user]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!connectionId) return;
    const channel = supabase
      .channel(`dm-${connectionId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_messages", filter: `connection_id=eq.${connectionId}` }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_message_reactions" }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [connectionId, fetchData]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if ((!newMsg.trim() && !photoPath) || !user || !connectionId || sending) return;
    setSending(true);
    await supabase.from("direct_messages").insert({
      connection_id: connectionId,
      sender_id: user.id,
      content: newMsg.trim() || "📷",
      photo_url: photoPath,
      reply_to_id: replyTo?.id || null,
    } as any);
    const { data: conn } = await supabase.from("connections").select("requester_id, recipient_id").eq("id", connectionId).maybeSingle();
    const recipientId = conn?.requester_id === user.id ? conn.recipient_id : conn?.requester_id;
    if (recipientId) {
      const { data: myProf } = await supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle();
      const body = `${myProf?.display_name ?? "Someone"} sent you a message`;
      await dispatchNotification({
        userId: recipientId,
        type: "direct_message",
        body,
        url: `/messages/${connectionId}`,
        entityType: "message",
        entityId: connectionId,
      });
    }
    setNewMsg("");
    setNewMsgMentions([]);
    setPhotoPath(null);
    setPhotoPreviewUrl(null);
    setReplyTo(null);
    setSending(false);
  };

  const deleteMsg = async (msgId: string) => {
    await supabase
      .from("direct_messages")
      .update({ content: "Message deleted", edited_at: new Date().toISOString() } as any)
      .eq("id", msgId);
    void fetchData();
  };

  const saveEdit = async (msgId: string) => {
    if (!editText.trim()) return;
    const existingMessage = messages.find((message) => message.id === msgId);
    const parsed = parseKindredShareContent(existingMessage?.content);
    await supabase
      .from("direct_messages")
      .update({ content: composeKindredContent(editText, parsed.payload), edited_at: new Date().toISOString() } as any)
      .eq("id", msgId);
    setEditingId(null);
    void fetchData();
  };

  const toggleMute = async () => {
    if (!connectionId) return;
    const newMuted = !isMuted;
    await supabase.from("connections").update({ is_muted: newMuted } as any).eq("id", connectionId);
    setIsMuted(newMuted);
    toast.success(newMuted ? "Conversation muted" : "Conversation unmuted");
  };

  const toggleReaction = async (messageId: string, emoji: string) => {
    if (!user) return;

    const existing = reactions.find(
      (reaction: any) => reaction.message_id === messageId && reaction.user_id === user.id && reaction.emoji === emoji,
    );

    if (existing?.id) {
      await supabase.from("direct_message_reactions").delete().eq("id", existing.id);
    } else {
      await supabase.from("direct_message_reactions").insert({ message_id: messageId, user_id: user.id, emoji });
    }

    void fetchData();
  };

  const formatTime = (ts: string) => new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const shouldShowTimestamp = (i: number) => {
    if (i === 0) return true;
    const prev = new Date(messages[i - 1].created_at);
    const curr = new Date(messages[i].created_at);
    return (curr.getTime() - prev.getTime()) > 3600000;
  };

  const getReplyMsg = (replyToId: string) => messages.find((m) => m.id === replyToId);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="kindred-screen-header shrink-0">
        <button onClick={() => navigate("/communities?section=messages")} className="text-secondary-foreground"><ArrowLeft size={22} /></button>
        {otherProfile && (
          <div className="flex flex-1 items-center gap-2">
            <Avatar className="h-8 w-8">
              {otherProfile.avatar_url && <AvatarImage src={otherProfile.avatar_url} />}
              <AvatarFallback className="bg-white/20 text-white text-xs font-semibold">
                {(otherProfile.display_name ?? "K")[0]}
              </AvatarFallback>
            </Avatar>
            <h1 className="kindred-screen-title">{otherProfile.display_name ?? "Member"}</h1>
          </div>
        )}
        <button onClick={toggleMute} className="text-secondary-foreground/80">
          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </header>

      <div className="flex-1 overflow-y-auto space-y-2 px-4 py-4">
        {!otherProfile && messages.length === 0 ? (
          <AppLoadingState title="Loading conversation" body="Pulling together your conversation." />
        ) : null}
        {otherProfile && messages.length === 0 ? (
          <AppEmptyState
            title={`Say hello to ${otherProfile.display_name ?? "this member"} when you're ready`}
            body="A first message can be simple. Start gently and take it one message at a time."
          />
        ) : null}
        {messages.map((msg, i) => {
          const isMine = msg.sender_id === user?.id;
          const canManageMessage = canManageOwnedContentOnly({ viewerId: user?.id, ownerId: msg.sender_id });
          const isDeleted = msg.content === "Message deleted";
          const repliedMsg = msg.reply_to_id ? getReplyMsg(msg.reply_to_id) : null;
          const messageReactions = groupScopedReactions(reactions, "message_id", msg.id, user?.id);
          const selectedReaction = Object.entries(messageReactions).find(([, value]) => value.mine)?.[0];
          return (
            <div key={msg.id}>
              {shouldShowTimestamp(i) && (
                <p className="py-2 text-center text-[10px] text-muted-foreground">
                  {new Date(msg.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} {formatTime(msg.created_at)}
                </p>
              )}
              <div
                className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                {...(!isDeleted ? longPressAction(() => setMessageActionsId(msg.id)) : {})}
              >
                <div className="flex items-end gap-1">
                  <div className="max-w-[75%]">
                    <div
                      className={`rounded-2xl px-3.5 py-2 text-sm ${
                        isMine
                          ? "bg-primary text-primary-foreground rounded-br-md"
                          : "bg-card text-foreground border border-border rounded-bl-md"
                      } ${isDeleted ? "italic opacity-60" : ""}`}
                    >
                      {repliedMsg && (
                        <div className="mb-1 rounded-lg bg-black/10 px-2 py-1 border-l-2 border-primary text-xs opacity-80">
                          <p className="font-medium">{repliedMsg.sender_id === user?.id ? "You" : otherProfile?.display_name}</p>
                          <p className="truncate">{getKindredSharePreview(repliedMsg.content)}</p>
                        </div>
                      )}
                      {editingId === msg.id ? (
                        <div className="space-y-1">
                          <AppInput value={editText} onChangeText={setEditText} />
                          <AppButtonRow>
                            <AppButton type="button" size="sm" onPress={() => saveEdit(msg.id)}>
                              Save
                            </AppButton>
                            <AppButton type="button" tone="outline" size="sm" onPress={() => setEditingId(null)}>
                              Cancel
                            </AppButton>
                          </AppButtonRow>
                        </div>
                      ) : (
                        <>
                          {msg.photo_url && (
                            <button onClick={() => setFullScreenImage(msg.photo_url)} className="mb-1 max-w-[200px] rounded-lg overflow-hidden">
                              <img src={msg.photo_url} alt="" className="w-full rounded-lg" />
                            </button>
                          )}
                          <KindredSharedContent content={msg.content} textClassName="text-sm" />
                          {msg.edited_at && !isDeleted ? (
                            <p className={`mt-0.5 text-[10px] ${isMine ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                              Edited
                            </p>
                          ) : null}
                        </>
                      )}
                      <p className={`mt-0.5 text-[10px] ${isMine ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                    {!isDeleted ? (
                      <ReactionBar
                        reactions={messageReactions}
                        showAddButton={false}
                        onToggleReaction={(emoji) => toggleReaction(msg.id, emoji)}
                        className={isMine ? "justify-end" : undefined}
                      />
                    ) : null}
                  </div>
                  {!isDeleted && (
                    <div className="flex flex-col gap-0.5">
                      <MessageActions
                        open={messageActionsId === msg.id}
                        onOpenChange={(open) => setMessageActionsId(open ? msg.id : null)}
                        selectedReaction={selectedReaction}
                        onReactionSelect={(emoji) => toggleReaction(msg.id, emoji)}
                        actions={[
                          {
                            id: "reply",
                            label: "Reply",
                            icon: <Reply size={16} />,
                            onSelect: () => setReplyTo(msg),
                          },
                          ...(!canManageMessage
                            ? [
                                {
                                  id: "report",
                                  label: "Report",
                                  icon: <Flag size={16} />,
                                  onSelect: () => {
                                    setReportMsgId(msg.id);
                                    setReportOpen(true);
                                  },
                                },
                              ]
                            : []),
                        ]}
                        onEdit={canManageMessage ? () => {
                          const parsed = parseKindredShareContent(msg.content);
                          setEditingId(msg.id);
                          setEditText(parsed.payload ? parsed.text : msg.content);
                        } : undefined}
                        onDelete={canManageMessage ? () => deleteMsg(msg.id) : undefined}
                        showEdit={canManageMessage}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="shrink-0 border-t border-border bg-card px-4 py-3">
        {replyTo && (
            <ReplyQuote
              name={replyTo.sender_id === user?.id ? "yourself" : otherProfile?.display_name ?? "Member"}
              preview={getKindredSharePreview(replyTo.content)}
              onCancel={() => setReplyTo(null)}
            />
          )}
        <PhotoUploadButton
          bucket="dm-photos"
          onPhotoReady={setPhotoPath}
          onPhotoPreviewReady={setPhotoPreviewUrl}
          onClear={() => {
            setPhotoPath(null);
            setPhotoPreviewUrl(null);
          }}
          previewUrl={photoPreviewUrl}
          returnObjectPath
        />
        <AppCard className="mt-2">
          <AppCardBody>
            <div className="flex items-center gap-2">
              <MentionComposerField
                value={newMsg}
                onChangeText={setNewMsg}
                mentions={newMsgMentions}
                onMentionsChange={setNewMsgMentions}
                lookupCandidates={async (query) => {
                  if (!otherProfile?.display_name?.trim()) {
                    return [];
                  }

                  const normalizedQuery = query.trim().toLowerCase();
                  const displayName = otherProfile.display_name.trim();
                  if (normalizedQuery && !displayName.toLowerCase().includes(normalizedQuery)) {
                    return [];
                  }

                  return [
                    {
                      id: otherProfile.id ?? "",
                      displayName,
                      avatarUrl: otherProfile.avatar_url ?? null,
                    },
                  ].filter((candidate) => candidate.id);
                }}
                placeholder={`Message ${otherProfile?.display_name ?? ""}...`}
                fieldProps={{ flex: 1 }}
                inputProps={{
                  onKeyDown: (event: any) => {
                    if (event.key === "Enter") {
                      void send();
                    }
                  },
                  flex: 1,
                }}
              />
              <AppButton
                size="md"
                onPress={send}
                disabled={sending || (!newMsg.trim() && !photoPath)}
                aria-label="Send message"
              >
                <Send size={16} />
              </AppButton>
            </div>
          </AppCardBody>
        </AppCard>
      </div>

      {fullScreenImage && (
        <FullScreenImage images={[fullScreenImage]} currentIndex={0} onClose={() => setFullScreenImage(null)} onNavigate={() => {}} />
      )}

      <ReportSheet open={reportOpen} onOpenChange={setReportOpen} contentType="dm" contentId={reportMsgId ?? undefined} />
    </div>
  );
};

export default MessageThread;
